import { VideoCoachingStatus } from "@components/videos/enums/VideoCoachingStatus.enum";
import { VideoReviewStatus } from "@components/videos/enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "@components/videos/enums/VideoReviewSubStatus.enum";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { DateRange } from "@enums/DateRange.enum";
import { SortType } from "@enums/SortType";
import { IDriver } from "@interfaces/IDriver";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IDriverFilter } from "@interfaces/filters/IDriverFilter";
import { IObservationFilter } from "@interfaces/filters/IObservationFilter";
import { ChartService } from "@services/charts.service";
import { ColourService } from "@services/colour.service";
import { DisplayService, IDialogOptionsJson } from "@services/display.service";
import { DriverService } from "@services/driver.service";
import { ObservationsService } from "@services/observations.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import * as moment from "moment";
import { IEventBarSegment } from "src/app/main/components/insights/fleetEvents/components/fleetEventBar/fleetEventBar.component";
import { VideoEventCode } from "../enums/VideoEventCode";
import { VideoObservation } from "../enums/VideoObservation";
import { VideoStatus } from "../enums/VideoStatus";
import { IVideoEntry } from "../models/IVideoEntry";
import { VideoEntryService } from "../services/video-entry.service";
import { VideoEventCodeService } from "../services/video-event-code.service";
import "./driverVideosPage.scss";

interface IComponentBindings {
}

class DriversVideosComponentController implements angular.IComponentController, IComponentBindings {

    loading = false;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IDriver>[] = [];

    drivers: IDriver[];
    displayDrivers: IDriver[];
    selectedDriver: IDriver;

    loadingVideos = false;
    videoEvents: IVideoEntry[] = [];

    dateFilter: IDateFilter;

    reviewStatusChartConfig: Highcharts.Options;
    coachingStatusChartConfig: Highcharts.Options;
    observationsChartConfig: Highcharts.Options;

    defaultDateRange: DateRange = DateRange.Latest7Days;

    searchString: string;

    static $inject = ["$q", "$scope", "driverService", "displayService", "observationsService", "chartService", "veriDate", "veriTableService", "colourService", "translationService", "videoEventCodeService", "videoEntryService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly driverService: DriverService,
        private readonly displayService: DisplayService,
        private readonly observationsService: ObservationsService,
        private readonly chartService: ChartService,
        private readonly veriDate: VeriDate,
        private readonly veriTableService: VeriTableService,
        private readonly colourService: ColourService,
        private readonly translationService: TranslationService,
        private readonly videoEventCodeService: VideoEventCodeService,
        private readonly videoEntryService: VideoEntryService) {
    }

    $onInit(): void {
        this.createTableHeaders();
        this.createChartConfigs();
        this.loadData();
    }

    dateFilterChanged(filter: IDateFilter) {
        this.dateFilter = filter;
        this.loadData(true);
    }

    driverFilterChanged(filter: IDriverFilter) {
        this.displayDrivers = [];

        if (!filter.driver) {
            this.displayDrivers = this.drivers;
        }
        else {
            this.displayDrivers = this.drivers.filter(driver => driver.Id == filter.driver.Id);
        }

        this.createTableRows();
        this.createChartSeries();
    }

    observationsFilterChanged(filter: IObservationFilter) {
        this.displayDrivers = [];

        if (!filter.observation) {
            this.displayDrivers = this.drivers;
        }
        else {
            this.filterDriversByObservations(filter);
        }

        this.createTableRows();
        this.createChartSeries();
    }

    driverClicked(driver: IDriver) {
        if (driver == this.selectedDriver) {
            this.driverClicked(null);
            return;
        }

        this.showDriverDialog(driver);
    }

    onSearch(searchString) {
        this.searchString = searchString;
        this.$scope.$applyAsync();
    }

    // ---

    private loadData(invalidateCache: boolean = false) {
        const driversPromise = this.driverService.getDrivers();

        let dateRange = this.veriDate.dateRangeFromEnum(this.defaultDateRange);

        if (this.dateFilter) {
            dateRange = this.dateFilter;
        }

        const entriesPromise = this.videoEntryService.getAllVideosEntries(
            dateRange.start, 
            dateRange.end, 
            { processEntries: true, invalidateCache: invalidateCache }
        ) as angular.IPromise<IVideoEntry[]>;

        this.loading = true;
        this.$q.all([driversPromise, entriesPromise]).then(([drivers, videoEntries]) => {

            this.drivers = drivers;
            this.displayDrivers = drivers;
            this.videoEvents = videoEntries.filter(entry => entry.DriverId && !this.videoEventCodeService.isMinorEvent(entry));

            this.displayDrivers.forEach(driver => {
                driver.videoEvents = videoEntries.filter(entry => entry.DriverId === driver.Id);
            });

            this.createTableRows();
            this.createChartSeries();

        }).finally(() => {
            this.loading = false;
        });
    }


    private showDriverDialog(driver: IDriver) {
        const dialogOptions: IDialogOptionsJson = {
            title: driver.DisplayName,
            dialogHTML: `<driver-video-card driver="$ctrl.driver" video-events='$ctrl.videoEvents' flex></vehicle-video-card>`,
            dialogId: "vehicleVideoCardDialog",
            bindings: {
                driver: driver,
                videoEvents: this.videoEvents
            }
        };

        this.displayService.showDialog(dialogOptions).then(() => {
            this.createChartConfigs();
            this.loadData();
        });
    }

    private createChartSeries() {
        this.createReviewChartSeries();
        this.createCoachingOutcomeChartSeries();

        this.createObservationsChartSeries();
    }

    private createObservationsChartSeries() {
        const events = this.videoEvents;

        const lastWeekStart = moment().subtract(15, "days");
        const thisWeekStart = moment().subtract(7, "days");
        const thisWeekEnd = moment();

        var observationSummary: { observation: VideoObservation, name: string, lastWeekCount: number, thisWeekCount: number, colour: string }[] = [];

        events.forEach(video => {
            video.MetaData.VideoObservations?.forEach(observation => {
                var existingSegment = observationSummary.filter(segment => segment.observation === observation)[0];

                if (!existingSegment) {
                    existingSegment = {
                        observation: observation,
                        name: this.observationsService.setObservationTitleForDisplay(observation),
                        lastWeekCount: 0,
                        thisWeekCount: 0,
                        colour: this.observationsService.getObservationsColour(observation)
                    };

                    observationSummary.push(existingSegment);
                }

                if (moment(video.EventTimeUtc).isAfter(thisWeekStart)) {
                    existingSegment.thisWeekCount++;
                }
                else {
                    existingSegment.lastWeekCount++;
                }
            });
        });

        const formattedLastWeekStart = this.veriDate.formatDate(lastWeekStart, "date");
        const formattedThisWeekStart = this.veriDate.formatDate(thisWeekStart, "date");
        const formattedThisWeekEnd = this.veriDate.formatDate(thisWeekEnd, "date");

        const lastWeekObservationsSeries: { name: string, y: number, color: string }[] = [];
        const thisWeekObservationsSeries: { name: string, y: number, color: string }[] = [];

        var categories: string[] = [];

        observationSummary.forEach(observation => {
            lastWeekObservationsSeries.push({ name: observation.name, y: observation.lastWeekCount, color: 'rgba(0, 0, 0, 0.2)' });
            thisWeekObservationsSeries.push({ name: observation.name, y: observation.thisWeekCount, color: observation.colour });

            categories.push(observation.name);
        });

        this.observationsChartConfig.series = [
            { name: `${formattedLastWeekStart} - ${formattedThisWeekStart}`, data: lastWeekObservationsSeries },
            { name: `${formattedThisWeekStart} - ${formattedThisWeekEnd}`, data: thisWeekObservationsSeries }
        ];

        this.observationsChartConfig.xAxis = {
            categories: categories
        };
    }

    private createReviewChartSeries() {
        const events = this.videoEvents.filter(v => v.Status == VideoStatus.Downloaded);
        const reviewStatusSeries: { name: string, y: number, color: string }[] = [];

        events.forEach(video => {
            var videoSeriesName = this.translationService.tEnum(VideoReviewStatus, "VideoReviewStatus", video.reviewStatus);
            var existingSeries = reviewStatusSeries.find(series => series.name === videoSeriesName);

            if (existingSeries) {
                existingSeries.y++;
            }
            else {
                reviewStatusSeries.push({
                    name: videoSeriesName,
                    y: 1,
                    color: videoSeriesName == this.translationService.tEnum(VideoReviewStatus, "VideoReviewStatus", VideoReviewStatus.NeedsReview)
                        ? this.colourService.orangeColor
                        : this.colourService.greenColor
                });
            }
        });

        this.reviewStatusChartConfig.series = [{ name: "", data: reviewStatusSeries }];
    }

    private createCoachingOutcomeChartSeries() {
        const events = this.videoEvents.filter(entry => entry.reviewStatus !== VideoReviewStatus.NeedsReview && (entry.reviewStatus !== VideoReviewStatus.Reviewed || entry.reviewSubStatus !== VideoReviewSubStatus.ActionRequired));

        const reviewStatusSeries: { name: string, y: number, color: string }[] = [];

        events.forEach(video => {
            var videoSeriesName = this.translationService.translateEnum(VideoReviewSubStatus, "VideoReviewSubStatus", video.reviewSubStatus);
            var existingSeries = reviewStatusSeries.find(series => series.name === videoSeriesName);

            if (existingSeries) {
                existingSeries.y++;
            }
            else {
                let color = 'rgba(0,0,0,0.1)';
                if (video.coachingStatus == VideoCoachingStatus.Praise) {
                    color = this.colourService.greenColor;
                }
                else if (video.coachingStatus == VideoCoachingStatus.DriverTrained) {
                    color = this.colourService.orangeColor;
                }

                reviewStatusSeries.push({
                    name: videoSeriesName,
                    y: 1,
                    color: color
                });
            }
        });

        this.coachingStatusChartConfig.series = [{ name: "", data: reviewStatusSeries }];
    }

    private createChartConfigs() {
        this.reviewStatusChartConfig = this.chartService.createChartOptions("pie");
        this.coachingStatusChartConfig = this.chartService.createChartOptions("pie");
        this.observationsChartConfig = this.chartService.createChartOptions("column");

        this.observationsChartConfig.plotOptions = {
            column: {
                borderColor: 'rgba(0,0,0,0.5)',
                borderWidth: 0
            }
        };

        var legendOptions: Highcharts.LegendOptions = {
            enabled: true
        }

        this.chartService.setLegendOptions(this.reviewStatusChartConfig, legendOptions);
        this.chartService.setLegendOptions(this.coachingStatusChartConfig, legendOptions);
    }

    private createTableHeaders() {
        this.tableHeaders = [
            { title: this.translationService.t("Common.Drivers"), sortable: true },
            { title: this.translationService.t("Videos.ReviewStatus"), width: 150, sortable: false },
            { title: this.translationService.tEnum(VideoReviewStatus, "VideoReviewStatus", VideoReviewStatus.NeedsReview), width: 140, sortable: true, sortType: SortType.Number },
            { title: this.translationService.tEnum(VideoReviewStatus, "VideoReviewStatus", VideoReviewStatus.Reviewed), width: 100, sortable: true, sortType: SortType.Number },
            { title: this.translationService.tEnum(VideoReviewStatus, "VideoReviewStatus", VideoReviewStatus.Coached), width: 100, sortable: true, sortType: SortType.Number },
            { title: this.translationService.t("DriverVideosPage.TopObservations"), sortable: true },
            { title: this.translationService.t("Videos.New"), width: 60, sortable: true, sortType: SortType.Number },
            { title: this.translationService.translate("cameraService.FatigueEvents"), width: 70, sortable: true, sortType: SortType.Number },
            { title: this.translationService.translate("cameraService.DmsEvents"), width: 60, sortable: true, sortType: SortType.Number },
            { title: this.translationService.translate("cameraService.AdasEvents"), width: 60, sortable: true, sortType: SortType.Number },
        ];
    }

    private createTableRows() {
        this.tableRows = [];

        if (!this.displayDrivers) {
            return;
        }

        this.tableRows = this.veriTableService.createTableRowsWithCells(this.displayDrivers, driver => this.createTableRowCells(driver));
    }

    private createTableRowCells(driver: IDriver): IVeriTableRowCell[] {
        const videoEvents = driver.videoEvents.filter(entry => !this.videoEventCodeService.isMinorEvent(entry));

        if (videoEvents.length == 0) {
            return null;
        }

        const needingReview = videoEvents.filter(v => v.reviewStatus === VideoReviewStatus.NeedsReview && v.Status == VideoStatus.Downloaded);
        const coachedActionTaken = videoEvents.filter(v => v.reviewStatus === VideoReviewStatus.Coached);
        const reviewed = videoEvents.filter(v => v.reviewStatus === VideoReviewStatus.Reviewed);
        const unseen = videoEvents.filter(v => !v.watched).length;
        const dmsEvents = videoEvents?.filter(event => event.EventCode !== VideoEventCode.DriverFatigue && this.videoEventCodeService.isDmsEvent(event.EventCode));
        const fatigueEvents = videoEvents?.filter(event => event.EventCode === VideoEventCode.DriverFatigue);
        const adasEvents = videoEvents?.filter(event => this.videoEventCodeService.isAdasEvent(event.EventCode));

        return [
            { contents: driver.Name, data: driver, sortData: driver.Name },
            this.createReviewActionCell(needingReview, reviewed, coachedActionTaken, needingReview.length + reviewed.length + coachedActionTaken.length),
            { contents: needingReview.length.toString(), sortData: needingReview.length },
            { contents: reviewed.length.toString(), sortData: reviewed.length },
            { contents: coachedActionTaken.length.toString(), sortData: coachedActionTaken.length },
            this.createTopObservationsCell(videoEvents),
            { contents: unseen.toString(), sortData: unseen },
            { contents: `${fatigueEvents.length}`, sortData: fatigueEvents.length },
            { contents: `${dmsEvents.length}`, sortData: dmsEvents.length },
            { contents: `${adasEvents.length}`, sortData: adasEvents.length },
        ];
    }

    private filterDriversByObservations(filter: IObservationFilter) {
        this.drivers.forEach(driver => {
            driver.videoEvents.forEach(videoEvent => {
                const foundObservation = videoEvent.MetaData?.VideoObservations.find(vc => vc == VideoObservation[filter.observation.label]);

                if (isNaN(foundObservation)) {
                    return;
                }

                if (!this.displayDrivers.find(displayDriver => displayDriver == driver)) {
                    this.displayDrivers.push(driver);
                }
            });
        });
    }

    private createTopObservationsCell(rowVideoEvents: IVideoEntry[]): IVeriTableRowCell {
        var topObservations: { total: number, label: string, observation: VideoObservation }[] = [];

        rowVideoEvents.forEach(video => {
            video.MetaData.VideoObservations?.forEach(observation => {
                var existingSegments = topObservations.filter(segment => segment.observation === observation);

                if (existingSegments.length > 0) {
                    existingSegments[0].total++;
                }
                else {
                    topObservations.push({
                        observation: observation,
                        label: this.observationsService.setObservationTitleForDisplay(observation),
                        total: 1,
                    });
                }
            });
        });

        topObservations.sort((a, b) => {
            return b.total - a.total;
        });

        const topObservationsLabel = this.addTopObservationLabels(topObservations.slice(0, 2));
        const topObservationsExport = this.addTopObservationLabels(topObservations);

        return {
            contents: topObservationsLabel,
            exportData: topObservationsExport
        };
    }

    private addTopObservationLabels(observations: { total: number, label: string, observation: VideoObservation }[]) {
        var text = "";

        observations.forEach(topObservation => {
            if (text.length) {
                text += ", ";
            }

            text += `${topObservation.label} <span class='text-muted font-size-11'>(${topObservation.total})</span>`;
        });

        return text;
    }

    private createReviewActionCell(needingReview: IVideoEntry[], reviewed: IVideoEntry[], coachedActionTaken: IVideoEntry[], total: number): IVeriTableRowCell {
        const actionSegments: IEventBarSegment<IVideoEntry>[] = [];
        actionSegments.push({
            id: actionSegments.length + 1,
            label: this.translationService.t("DriverVideosPage.NeedingReview"),
            color: this.colourService.orangeColor,
            events: needingReview
        });

        actionSegments.push({
            id: actionSegments.length + 1,
            label: this.translationService.t("DriverVideosPage.Reviewed"),
            color: this.colourService.greenColor,
            events: reviewed.concat(coachedActionTaken)
        });

        const actionBarChart =
            `<fleet-event-bar
                flex
                segments="cell.data.segments"
                label="${this.translationService.t('DriverVideosPage.Actions')}"
                total="cell.data.total"
                max-total="cell.data.total"
                hide-total="true"
                show-segment-count="true">
            </fleet-event-bar>`;

        return { contents: actionBarChart, data: { segments: actionSegments, total: total }, exportData: this.createActionSegmentsExport(actionSegments) }
    }

    private createActionSegmentsExport(actionSegments: IEventBarSegment<IVideoEntry>[]) {
        var text = "";

        actionSegments.forEach(segment => {
            text += `${segment.label} (${segment.events.length}) `;
        });

        return text;
    }
}

export class DriverVideosPageComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = DriversVideosComponentController;
        this.template = require("./driverVideosPage.html");
    }
}
